import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CalendarDays, Clock, XCircle, ChevronRight, MessageSquare, TrendingUp } from "lucide-react";
import { format, parseISO, differenceInHours } from "date-fns";
import { sv } from "date-fns/locale";
import { toast } from "@/hooks/use-toast";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import PlayerSessionDetail from "./PlayerSessionDetail";
import ParentMessageThread from "./ParentMessageThread";
import PlayerFeedbackHistory from "./PlayerFeedbackHistory";
import PlayerRadarChart from "./PlayerRadarChart";

const CANCELLATION_HOURS = 24;

export default function ParentDashboard() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedChild, setSelectedChild] = useState<string | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [showMessages, setShowMessages] = useState<{ groupId: string; coachId: string; childId: string } | null>(null);
  const today = new Date().toISOString().split("T")[0];

  // Fetch children linked via parent_id
  const { data: children = [], isLoading: loadingChildren } = useQuery({
    queryKey: ["parent-children", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("group_members")
        .select("player_id, profiles!group_members_player_id_fkey(id, full_name), training_groups(id, name, coach_id, color)")
        .eq("parent_id", user!.id)
        .eq("status", "active");
      if (error) throw error;
      // Deduplicate by player_id, collect groups
      const map = new Map<string, { id: string; name: string; groups: { id: string; name: string; coach_id: string | null; color: string | null }[] }>();
      (data ?? []).forEach((d: Record<string, unknown>) => {
        const pid = d.player_id;
        if (!map.has(pid)) {
          map.set(pid, { id: pid, name: d.profiles?.full_name || "Okänd", groups: [] });
        }
        if (d.training_groups) {
          map.get(pid)!.groups.push(d.training_groups);
        }
      });
      return Array.from(map.values());
    },
  });

  const activeChildId = selectedChild || children[0]?.id || null;
  const activeChild = children.find((c) => c.id === activeChildId);

  // Upcoming sessions for selected child
  const { data: sessions = [] } = useQuery({
    queryKey: ["parent-child-sessions", activeChildId],
    enabled: !!activeChildId,
    queryFn: async () => {
      const { data } = await supabase
        .from("session_attendance")
        .select(
          "id, rsvp_status, session_id, training_sessions!inner(id, session_date, start_time, duration_minutes, status, training_groups(name, color), courts(name))"
        )
        .eq("player_id", activeChildId!)
        .gte("training_sessions.session_date", today)
        .order("created_at", { ascending: true });
      return (data ?? []).sort((a: Record<string, unknown>, b: Record<string, unknown>) =>
        (a.training_sessions?.session_date ?? "").localeCompare(b.training_sessions?.session_date ?? "")
      );
    },
  });

  // Attendance stats for child
  const { data: stats } = useQuery({
    queryKey: ["parent-child-stats", activeChildId],
    enabled: !!activeChildId,
    queryFn: async () => {
      const { data } = await supabase
        .from("session_attendance")
        .select("actual_status, training_sessions!inner(status)")
        .eq("player_id", activeChildId!)
        .eq("training_sessions.status", "completed");
      if (!data) return { pct: 0, total: 0, attended: 0 };
      const total = data.length;
      const attended = data.filter((s: Record<string, unknown>) => ["present", "late"].includes(s.actual_status)).length;
      return { pct: total > 0 ? Math.round((attended / total) * 100) : 0, total, attended };
    },
  });

  // Past sessions with coach notes
  const { data: pastSessions = [] } = useQuery({
    queryKey: ["parent-child-past", activeChildId],
    enabled: !!activeChildId,
    queryFn: async () => {
      const { data } = await supabase
        .from("session_attendance")
        .select(
          "session_id, actual_status, training_sessions!inner(id, session_date, start_time, summary, coach_note, focus_areas, status, training_groups(name, color))"
        )
        .eq("player_id", activeChildId!)
        .eq("training_sessions.status", "completed")
        .lte("training_sessions.session_date", today)
        .order("created_at", { ascending: false })
        .limit(10);
      return (data ?? []).sort((a: Record<string, unknown>, b: Record<string, unknown>) =>
        (b.training_sessions?.session_date ?? "").localeCompare(a.training_sessions?.session_date ?? "")
      );
    },
  });

  const cancelMutation = useMutation({
    mutationFn: async (attendanceId: string) => {
      const { error } = await supabase
        .from("session_attendance")
        .update({ rsvp_status: "not_attending", cancelled_by: user!.id, cancelled_at: new Date().toISOString() })
        .eq("id", attendanceId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["parent-child-sessions"] });
      toast({ title: "Avanmälan registrerad" });
    },
    onError: () => toast({ title: "Kunde inte avanmäla", variant: "destructive" }),
  });

  if (showMessages) {
    return (
      <ParentMessageThread
        groupId={showMessages.groupId}
        coachId={showMessages.coachId}
        childId={showMessages.childId}
        onBack={() => setShowMessages(null)}
      />
    );
  }

  if (selectedSessionId) {
    return <PlayerSessionDetail sessionId={selectedSessionId} onBack={() => setSelectedSessionId(null)} />;
  }

  if (loadingChildren) return <p className="text-sm text-muted-foreground">Laddar...</p>;
  if (children.length === 0) return <p className="text-sm text-muted-foreground">Inga barn kopplade till ditt konto.</p>;

  return (
    <div className="space-y-4">
      {/* Child selector */}
      {children.length > 1 && (
        <Tabs value={activeChildId || undefined} onValueChange={setSelectedChild}>
          <TabsList>
            {children.map((c) => (
              <TabsTrigger key={c.id} value={c.id}>{c.name}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {activeChild && (
        <>
          <h2 className="text-lg font-semibold">{activeChild.name}</h2>

          {/* Attendance */}
          {stats && stats.total > 0 && (
            <Card>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{stats.attended} av {stats.total} pass</span>
                  <span className="font-semibold text-primary">{stats.pct}%</span>
                </div>
                <Progress value={stats.pct} className="h-2.5" />
              </CardContent>
            </Card>
          )}

          {/* Message coaches */}
          {activeChild.groups.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-sm font-semibold">Kontakta tränare</h3>
              <div className="flex gap-2 flex-wrap">
                {activeChild.groups.map((g: { id: string; name: string; coach_id: string | null; color: string | null }) => (
                  g.coach_id && (
                    <Button
                      key={g.id}
                      variant="outline"
                      size="sm"
                      onClick={() => setShowMessages({ groupId: g.id, coachId: g.coach_id, childId: activeChild.id })}
                    >
                      <MessageSquare className="h-3.5 w-3.5 mr-1" /> {g.name}
                    </Button>
                  )
                ))}
              </div>
            </section>
          )}

          {/* Upcoming sessions */}
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Kommande pass</h3>
            {sessions.length === 0 && <p className="text-sm text-muted-foreground">Inga kommande pass.</p>}
            {sessions.map((a: Record<string, unknown>) => {
              const s = a.training_sessions;
              if (!s) return null;
              const isCancelled = a.rsvp_status === "not_attending";
              const deadlineOk = differenceInHours(parseISO(`${s.session_date}T${s.start_time}`), new Date()) >= CANCELLATION_HOURS;

              return (
                <Card key={a.id} className={`cursor-pointer hover:bg-muted/50 ${isCancelled ? "opacity-60" : ""}`} onClick={() => setSelectedSessionId(s.id)}>
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {s.training_groups?.color && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.training_groups.color }} />}
                          <span className="font-medium text-sm">{s.training_groups?.name}</span>
                          <Badge variant={isCancelled ? "destructive" : "outline"} className="text-xs">{isCancelled ? "Avanmäld" : "Anmäld"}</Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                          <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{format(parseISO(s.session_date), "EEE d MMM", { locale: sv })}</span>
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{s.start_time?.slice(0, 5)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!isCancelled && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={(e) => e.stopPropagation()} disabled={!deadlineOk}>
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Avanmäla {activeChild.name}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  {activeChild.name} avanmäls från passet {format(parseISO(s.session_date), "d MMM", { locale: sv })} kl {s.start_time?.slice(0, 5)}.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                                <AlertDialogAction onClick={() => cancelMutation.mutate(a.id)}>Avanmäl</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </section>

          {/* Past sessions with coach notes */}
          {pastSessions.length > 0 && (
            <section className="space-y-2">
              <h3 className="text-sm font-semibold">Genomförda pass</h3>
              {pastSessions.map((a: Record<string, unknown>) => {
                const s = a.training_sessions;
                if (!s) return null;
                return (
                  <Card key={a.session_id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedSessionId(s.id)}>
                    <CardContent className="py-3 px-4 space-y-1">
                      <div className="flex items-center gap-2 text-sm">
                        {s.training_groups?.color && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.training_groups.color }} />}
                        <span className="font-medium">{s.training_groups?.name}</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">{format(parseISO(s.session_date), "d MMM", { locale: sv })}</span>
                      </div>
                      {s.coach_note && <p className="text-xs italic text-muted-foreground">"{s.coach_note}"</p>}
                      {s.focus_areas && (s.focus_areas as string[]).length > 0 && (
                        <div className="flex gap-1 flex-wrap">
                          {(s.focus_areas as string[]).map((f: string) => <Badge key={f} variant="secondary" className="text-[10px]">{f}</Badge>)}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </section>
          )}

          {/* Development section */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold flex items-center gap-1">
              <TrendingUp className="h-3.5 w-3.5" /> Utveckling
            </h3>
            <PlayerRadarChart playerId={activeChild.id} />
            <h4 className="text-sm font-semibold">Tränarens feedback</h4>
            <PlayerFeedbackHistory playerId={activeChild.id} />
          </section>
        </>
      )}
    </div>
  );
}
